import { Map } from 'immutable'
import fetchJSON from 'lib/fetchJSON'
import { pending, fulfilled, rejected } from 'lib/promiseMiddlewareHelpers'
import { EmbedData, serializeEmbedData, deserializeEmbedData } from 'lib/api/records'
import _ from 'lodash'
import { getConsole } from 'lib/getConsole'
const console = getConsole('regional embed', 'reducer')

export const LOAD_RSO = 'regional/LOAD_RSO'
export const LOAD_AVAILABLE_TAGS = 'regional/LOAD_AVAILABLE_TAGS'
export const CREATE_RSO = 'regional/CREATE_RSO'
export const SAVE_RSO = 'regional/SAVE_RSO'
export const LOAD_GEOJSON = 'regional/LOAD_GEOJSON'
export const TOGGLE_POITAG_VISIBILITY = 'regional/TOGGLE_POITAG_VISIBILITY'
export const REMOVE_POI_CATEGORY_TAG = 'regional/REMOVE_POITAG'
export const UPDATE_ROUTE = 'regional/UPDATE_ROUTE'
export const PICK_ROUTE_TAG = 'regional/PICK_ROUTE_TAG'
export const REMOVE_ROUTE_TAG = 'regional/REMOVE_ROUTE_TAG'
export const UPDATE_METRICUNITS = 'regional/UPDATE_METRICUNITS'
export const UPDATE_OVERLAY = 'regional/UPDATE_OVERLAY'
export const UPDATE_BOUNDS_CRITERIA = 'regional/UPDATE_BOUNDS_CRITERIA'
export const ADD_POI_CATEGORY_TAG = 'regional/ADD_POI_CATEGORY_TAG'
export const ADD_RESTRICTIVE_POI_TAG = 'regional/ADD_RESTRICTIVE_POI_TAG'
export const REMOVE_RESTRICTIVE_POI_TAG = 'regional/REMOVE_RESTRICTIVE_POI_TAG'
export const UPDATE_NAME = 'regional/UPDATE_NAME'

const initialState = Map({
  saving: false,
  embedData: EmbedData(),
  geoJSON: null,
  availableRouteTags: [],
  availablePoiTags: [],

  // URL overrides
  type: 'region',

  // New format
  routes: Map() // id -> routeObj
})

export const reducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case fulfilled(LOAD_RSO):
      const embed = deserializeEmbedData(action.payload)
      return state.withMutations(s => {
        s.set('routes', processRoutes(embed))
        s.set('embedData', embed)
      })
    case rejected(LOAD_GEOJSON):
      console.warn(`unable to load geoJSON for ${action.meta.embedId}`)
      return state
    case fulfilled(LOAD_GEOJSON):
      return state.set('geoJSON', action.payload)
    case fulfilled(LOAD_AVAILABLE_TAGS):
      return state.withMutations(s => {
        s.set('availablePoiTags', action.payload.poi_tags)
        s.set('availableRouteTags', action.payload.route_tags)
      })
    case rejected(LOAD_AVAILABLE_TAGS):
      console.warn('unable to load available tags')
      return state
    case fulfilled(CREATE_RSO):
    case fulfilled(SAVE_RSO):
      const savedEmbed = deserializeEmbedData(action.payload)
      return state.withMutations(s => {
        s.set('routes', processRoutes(savedEmbed))
        s.set('saving', false) // TODO this won't work if multiple requests are out
        s.set('embedData', savedEmbed)
      })
    case pending(SAVE_RSO):
      return state.set('saving', true)
    case rejected(SAVE_RSO):
      return state.set('saving', false)
    case TOGGLE_POITAG_VISIBILITY:
      const enabledPoiTags = state.getIn(['embedData', 'enabledPoiTags'])
      const newTags = action.visible ? enabledPoiTags.add(action.tag) : enabledPoiTags.delete(action.tag)
      return state.setIn(['embedData', 'enabledPoiTags'], newTags)
    case UPDATE_ROUTE:
      const urls = state.getIn(['embedData', 'urls']).Route
      const colors = state.getIn(['embedData', 'colors']).Route
      const newColors = { Route: action.color ? Object.assign({}, colors, { [action.routeId]: action.color }) : _.omit(colors, action.routeId) }
      const newUrls = { Route: action.url ? Object.assign({}, urls, { [action.routeId]: action.url }) : _.omit(urls, action.routeId) }
      return state.setIn(['embedData', 'colors'], newColors).setIn(['embedData', 'urls'], newUrls)
    case pending(REMOVE_ROUTE_TAG):
      return state
    case UPDATE_METRICUNITS:
      return state.setIn(['embedData', 'embedSettings', 'metricUnits'], action.metricUnits)
    case UPDATE_OVERLAY:
      return state.setIn(['embedData', 'embedSettings', 'overlay'], action.overlay)
    case UPDATE_BOUNDS_CRITERIA:
      return state.setIn(['embedData', 'boundsCriteria'], action.boundsCriteria)
    case PICK_ROUTE_TAG:
      const { tag } = action
      return state.merge({
        'embedData': state.get('embedData').set('routeTag', tag)
      })
    case REMOVE_ROUTE_TAG:
      const rsoWithoutTag = state.get('embedData').withMutations(r => {
        r.set('assets', null)
        r.set('routeTag', null)
      })
      return state.withMutations(s => {
        s.set('embedData', rsoWithoutTag)
        s.set('routes', Map())
        s.set('geoJSON', null)
      })
    case REMOVE_POI_CATEGORY_TAG:
      const poiTagsWithout = _.reject(state.getIn(['embedData', 'poiTags']), (t) => t.tag === action.tag)
      return state.setIn(['embedData', 'poiTags'], poiTagsWithout)
    case ADD_POI_CATEGORY_TAG:
      const poiTagsWithNew = _.uniqBy(_.concat(state.getIn(['embedData', 'poiTags']), { tag: action.tag }), o => o.tag)
      const enabledPoiTagsWithNew = state.getIn(['embedData', 'enabledPoiTags']).add(action.tag)
      return state.withMutations(s => {
        s.setIn(['embedData', 'enabledPoiTags'], enabledPoiTagsWithNew)
        s.setIn(['embedData', 'poiTags'], poiTagsWithNew)
        s.setIn(['embedData', 'embedSettings', 'poiBar'], true)
      })
    case REMOVE_RESTRICTIVE_POI_TAG:
      return state.setIn(['embedData', 'restrictivePoiTag'], null)
    case ADD_RESTRICTIVE_POI_TAG:
      return state.setIn(['embedData', 'restrictivePoiTag'], action.tag)
    case UPDATE_NAME:
      return state.setIn(['embedData', 'name'], action.name)
    default:
      return state
  }
}

export const loadRSO = (embedId, refresh) => ({
  type: LOAD_RSO,
  meta: { embedId },
  payload: fetchJSON(`/route_set_overviews/${embedId}.json?include_details=1${refresh ? '&refresh=1' : ''}`)
})

export const loadGeoJSON = (embedId, updatedAt) => ({
  type: LOAD_GEOJSON,
  meta: { embedId },
  payload: fetchJSON(`/cos/route_set_overviews/simplified_route/${embedId}.json?updated_at=${updatedAt}`)
})

const loadAvailableTags = (clubId) => ({
  type: LOAD_AVAILABLE_TAGS,
  clubId,
  payload: fetchJSON(`/organizations/${clubId}/available_tags.json`)
})

const createRSO = () => ({
  type: CREATE_RSO,
  payload: fetchJSON('/route_set_overviews.json', {}, { method: 'POST' })
})

const loadRsoAndTags = (embedId) => (dispatch, getState) => {
  return dispatch(loadRSO(embedId, true)).then(() => {
    const clubId = getState().regionalEmbed.getIn(['embedData', 'clubId'])
    return dispatch(loadAvailableTags(clubId))
  })
}

export const loadOrCreateRSO = (embedId) => (dispatch) => {
  if (!embedId) {
    return dispatch(createRSO()).then((response) => {
      var newId = response.action.payload.route_set_overview.id
      dispatch(loadRsoAndTags(newId))
    })
  }

  return dispatch(loadRsoAndTags(embedId))
}

export const togglePoiTagVisibility = (tag, visible) => (dispatch) => {
  dispatch({
    type: TOGGLE_POITAG_VISIBILITY,
    tag,
    visible
  })
  return dispatch(saveEmbed())
}

export const updateRoute = (routeId, { color, url }) => (dispatch, getState) => {
  dispatch({
    type: UPDATE_ROUTE,
    routeId,
    color,
    url
  })
  return dispatch(saveEmbed())
}

export const updateMetricUnits = (metricUnits) => (dispatch) => {
  dispatch({
    type: UPDATE_METRICUNITS,
    metricUnits
  })
  return dispatch(saveEmbed())
}

export const updateOverlay = (overlay) => (dispatch) => {
  dispatch({
    type: UPDATE_OVERLAY,
    overlay
  })
  return dispatch(saveEmbed())
}

export const updateBoundsCriteria = (boundsCriteria) => (dispatch) => {
  dispatch({
    type: UPDATE_BOUNDS_CRITERIA,
    boundsCriteria
  })
  return dispatch(saveEmbed())
}

export const pickRouteTag = (tag) => (dispatch) => {
  dispatch({
    type: PICK_ROUTE_TAG,
    tag
  })
  return dispatch(saveEmbed())
}

export const removeRouteTag = () => (dispatch) => {
  dispatch({
    type: REMOVE_ROUTE_TAG
  })
  return dispatch(saveEmbed())
}

export const addCategoryPoiTag = (tag) => (dispatch) => {
  dispatch({
    type: ADD_POI_CATEGORY_TAG,
    tag
  })
  return dispatch(saveEmbed())
}

export const removePoiTag = (tag) => (dispatch) => {
  dispatch({
    type: REMOVE_POI_CATEGORY_TAG,
    tag
  })
  return dispatch(saveEmbed())
}

export const addRestrictivePoiTag = (tag) => (dispatch) => {
  dispatch({
    type: ADD_RESTRICTIVE_POI_TAG,
    tag
  })
  return dispatch(saveEmbed())
}

export const removeRestrictivePoiTag = () => (dispatch) => {
  dispatch({
    type: REMOVE_RESTRICTIVE_POI_TAG
  })
  return dispatch(saveEmbed())
}

export const updateName = (name) => (dispatch) => {
  dispatch({
    type: UPDATE_NAME,
    name: name
  })
  return dispatch(saveEmbed())
}

const saveEmbed = () => (dispatch, getState) => {
  const state = getState()
  const embedData = state.regionalEmbed.get('embedData')
  const rsoId = embedData.get('id')

  return dispatch({
    type: SAVE_RSO,
    payload: fetchJSON(`/route_set_overviews/${rsoId}.json`, serializeEmbedData(embedData), { method: 'PUT' })
  })
}

/* Helpers */
const processRoutes = (embed) => {
  const rawRoutes = embed.get('assets', {}).route || {}
  return Map().withMutations((m) =>
    _.each(rawRoutes, (r, id) => {
      const color = embed.getIn(['colors', 'Route', id])
      const url = embed.getIn(['urls', 'Route', id])
      m.set(id, {
        id,
        name: r.name,
        color,
        url
      })
    })
  )
}
